import { useContext, useState } from 'react';
import { useNavigation } from '../../../utils/redirectUtils';
import { SourceContext } from '../../../utils/sourceContext';
import Header from '../../.shared/header/header';
import InputSelect from '../../.shared/InputSelect/InputSelect';
import Footer from '../../.shared/footer/footer';
import PatientStories from '../../.shared/PatientStories/PatientStories';
import FAQ from '../../.shared/FAQ/FAQ';
import AsSeenOn from '../../.shared/AsSeenOn/AsSeenOn';
import HowItWorks from '../../.shared/HowItWorks/HowItWorks';
import WhatsIncluded from '../../.shared/WhatsIncluded/WhatsIncluded';
import PriceUpdateBanner from '../../Landing/PriceUpdateBanner/PriceUpdateBanner';
import Pricing from '../../../data/Pricing';
import { US_STATES } from '../../../data/usStates';
import { trackPostHogEvent } from '../../../utils/posthogUtils';
import { startInterview } from '../../../utils/interviewFlow';
import styles from './BaseLandingV5.module.css';
import quoteAvatar from '../../../assets/hero-image.webp';

const PROGRAM_OPTIONS = ['Semaglutide', 'Tirzepatide', 'Not sure'];

export default function BaseLandingV5() {
    const { switchAndSave } = useContext(SourceContext);
    const navigate = useNavigation();

    const pricing = Pricing();
    const semaglutide = pricing.getPricingOptionsByProductName('semaglutide');
    const startingPrice = semaglutide?.injectable?.threeMonths?.monthly_amount?.split('.')[0] ?? null;

    const [selectedState, setSelectedState] = useState('');
    const [selectedProgram, setSelectedProgram] = useState(PROGRAM_OPTIONS[0]);

    const handleEligibility = () => {
        // Enroll before tracking, so trackExposure has registered the variant
        // on this person and the submit event below carries it.
        const flow = startInterview();

        trackPostHogEvent('landing_page_submitted', {
            selected: 'check_eligibility',
            eligibility_state: selectedState || undefined,
            eligibility_program: selectedProgram,
            to_path: '/interview',
            interview_flow: flow.id
        });
        navigate(flow.entryPath);
    };

    return (
        <div className={styles.page}>
            {switchAndSave && <PriceUpdateBanner />}

            <Header hideNavigation />

            <section className={styles.heroC}>
                <div className={styles.heroCInner}>
                    <div className={styles.heroCCopy}>
                        <div className={styles.heroCPill}>New lower prices</div>
                        <h1 className={styles.heroCTitle}>GLP-1 that fits your life — and your budget.</h1>
                        <p className={styles.heroCSub}>
                            One flat price for every dose, from a clinician who knows your history.
                            Most prescriptions need no telehealth visit at all.
                        </p>

                        <div className={styles.heroCStats}>
                            <div className={styles.heroCStat}>
                                <div className={styles.heroCStatNum}>${startingPrice ?? '—'}<span className={styles.heroCStatUnit}>/mo</span></div>
                                <div className={styles.heroCStatLabel}>Semaglutide, all doses</div>
                            </div>
                            <div className={styles.heroCStat}>
                                <div className={styles.heroCStatNum}>15–26<span className={styles.heroCStatUnit}>%</span></div>
                                <div className={styles.heroCStatLabel}>Body weight lost by real patients</div>
                            </div>
                            <div className={styles.heroCStat}>
                                <div className={styles.heroCStatNum}>2<span className={styles.heroCStatUnit}> min</span></div>
                                <div className={styles.heroCStatLabel}>To finish your evaluation</div>
                            </div>
                        </div>

                        <div className={styles.heroCQuote}>
                            <img src={quoteAvatar} alt="" aria-hidden="true" />
                            <p>
                                “I stopped waiting for the perfect moment. Nine months later I'm down 22%.”
                                <span className={styles.heroCQuoteWho}>— Kayleigh, patient since 2024</span>
                            </p>
                        </div>
                    </div>

                    <div className={styles.heroCCard}>
                        <h2 className={styles.heroCCardTitle}>Check your eligibility</h2>
                        <p className={styles.heroCCardSub}>Two questions now, the clinical evaluation next. No card required.</p>

                        <div className={styles.heroCField}>
                            <InputSelect
                                name="eligibilityState"
                                label="Your state"
                                value={selectedState}
                                onChange={(e) => setSelectedState(e.target.value)}
                                options={US_STATES}
                                placeholder="Select a state"
                            />
                        </div>

                        <div className={styles.heroCField}>
                            <span className={styles.heroCFieldLabel}>Which program interests you</span>
                            <div className={styles.heroCChips}>
                                {PROGRAM_OPTIONS.map((option) => (
                                    <button
                                        key={option}
                                        type="button"
                                        className={`${styles.heroCChip} ${selectedProgram === option ? styles.heroCChipActive : ''}`}
                                        aria-pressed={selectedProgram === option}
                                        onClick={() => setSelectedProgram(option)}
                                    >
                                        {option}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <button type="button" className={styles.heroCCta} onClick={handleEligibility}>
                            Start My Evaluation
                        </button>
                        <p className={styles.heroCCardFine}>
                            A licensed clinician determines whether treatment is appropriate. Cancel anytime.
                        </p>
                    </div>
                </div>
            </section>

            <AsSeenOn />

            <HowItWorks />

            <WhatsIncluded />

            <PatientStories />

            <FAQ />

            <Footer />
        </div>
    );
}
